import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Loader2, Eye, EyeOff, Phone, Lock, User, Mail, ArrowLeft } from 'lucide-react';

const RegisterScreen = () => {
  const { register, loading } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_number: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.name || !formData.email || !formData.mobile_number || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    const { confirmPassword, ...userData } = formData;
    const result = await register(userData);
    
    if (result.success) {
      navigate('/');
    } else {
      setError(result.message);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-dark-bg" data-testid="register-screen">
      {/* Header */}
      <div className="flex items-center px-4 py-4">
        <button
          onClick={() => navigate('/login')}
          className="p-2 rounded-xl bg-dark-surface text-white hover:bg-dark-card transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Create Account</h1>
          <p className="text-muted-text">Register to manage your properties</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <label className="text-sm text-muted-text">Full Name</label>
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-text" />
              <input
                type="text"
                name="name"
                data-testid="name-input"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name"
                className="w-full pl-12 pr-4 py-4 bg-dark-surface rounded-xl border border-dark-border text-white placeholder-muted-text focus:outline-none focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan transition-all"
              />
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <label className="text-sm text-muted-text">Email</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-text" />
              <input
                type="email"
                name="email"
                data-testid="email-input"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                className="w-full pl-12 pr-4 py-4 bg-dark-surface rounded-xl border border-dark-border text-white placeholder-muted-text focus:outline-none focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan transition-all"
              />
            </div>
          </div>

          {/* Mobile Number */}
          <div className="space-y-2">
            <label className="text-sm text-muted-text">Mobile Number</label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-text" />
              <input
                type="tel"
                name="mobile_number"
                data-testid="mobile-input"
                value={formData.mobile_number}
                onChange={handleChange}
                placeholder="Enter mobile number"
                className="w-full pl-12 pr-4 py-4 bg-dark-surface rounded-xl border border-dark-border text-white placeholder-muted-text focus:outline-none focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan transition-all"
              />
            </div>
          </div>

          {/* Password */}
          <div className="space-y-2">
            <label className="text-sm text-muted-text">Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-text" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                data-testid="password-input"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create password"
                className="w-full pl-12 pr-12 py-4 bg-dark-surface rounded-xl border border-dark-border text-white placeholder-muted-text focus:outline-none focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-text hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="space-y-2">
            <label className="text-sm text-muted-text">Confirm Password</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-text" />
              <input
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                data-testid="confirm-password-input"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm password"
                className="w-full pl-12 pr-4 py-4 bg-dark-surface rounded-xl border border-dark-border text-white placeholder-muted-text focus:outline-none focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan transition-all"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl" data-testid="error-message">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          {/* Register Button */}
          <button
            type="submit"
            data-testid="register-button"
            disabled={loading}
            className="w-full py-4 bg-[#1B2A7B] text-white font-semibold rounded-xl shadow-premium hover:bg-[#152163] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        {/* Login Link */}
        <p className="mt-6 text-center text-muted-text text-sm">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-neon-cyan hover:text-neon-magenta transition-colors font-medium"
          >
            Sign In
          </button>
        </p>
      </div>
    </div>
  );
};

export default RegisterScreen;
